import {create} from 'zustand';
import {persist} from 'zustand/middleware';

const DEFAULT_SETTINGS = {
    showTranslation: true,
    showCommentary: true,
    showPinyin: false,
    focusMode: false,
    lineHeight: 2.2,
    fontFamily: "'Noto Serif SC','Songti SC','STSong',serif",
    themeLabel: "宣纸米白",
};

const useZustandStore = create(
    persist(
        (set, get) => ({
            // 状态
            fontSize: 17,
            progress: 0,
            bookmarked: false,
            activeBook: "孟子",
            activeBookId: 2,
            activeChar: null,
            tooltip: {char: null, pos: null},
            activeAnnotation: null,  // { text, note }  | null
            settings: DEFAULT_SETTINGS,
            currentChapter: "一",
            charDataMap: {},

            // ── 章节内容（来自 API，不持久化） ──
            chapters: [],
            activeChapterId: null,
            chapterBlocks: [],
            chapterTitle: '',
            chapterOrd: 0,
            chapterTotal: 0,
            prevChapterId: null,
            nextChapterId: null,

            // 动作 (Actions)
            setFontSize: (newSizeOrFn) =>
                set((state) => ({
                    fontSize: typeof newSizeOrFn === 'function'
                        ? newSizeOrFn(state.fontSize)
                        : newSizeOrFn,
                })),

            setProgress: (progress) => set({progress}),

            toggleBookmark: () =>
                set((state) => ({bookmarked: !state.bookmarked})),

            setActiveBook: (book, bookId) => set({activeBook: book, activeBookId: bookId, charDataMap: {}}),

            setActiveChar: (char, event) =>
                set({
                    activeChar: char,
                    tooltip: char
                        ? {char, pos: {x: event.clientX, y: event.clientY}}
                        : {char: null, pos: null},
                }),

            // 更细粒度的 tooltip 更新
            setTooltip: (char, pos) => set({tooltip: {char, pos}}),

            setActiveAnnotation: (data) => set({activeAnnotation: data}),

            updateSetting: (key, value) =>
                set((state) => ({
                    settings: {...state.settings, [key]: value},
                })),

            setCurrentChapter: (chapter) => set({currentChapter: chapter}),

            // ── 章节数据 Actions ──
            setChapters: (chapters) => set({chapters}),
            setActiveChapterId: (id) => set({activeChapterId: id}),
            setChapterData: (data) => {
                // 只加载当前章节的字符释义，不合并旧数据
                const chars = data.chars || {};
                set({
                    chapterBlocks: data.blocks || [],
                    chapterTitle: data.title || '',
                    chapterOrd: data.ord || 0,
                    chapterTotal: data.total || 0,
                    prevChapterId: data.prev_id || null,
                    nextChapterId: data.next_id || null,
                    charDataMap: chars,
                });
            },

            // 如果需要批量更新设置（如主题切换）
            applyTheme: (themeVars) => {
                // 这里处理 CSS 变量注入，可以保留在组件中，或者将 DOM 操作也移到 store 的 action 中
                set((state) => ({
                    settings: {...state.settings, themeLabel: themeVars.label},
                }));
                // 注意：直接操作 DOM 的代码（如设置 CSS 变量）最好放在 useEffect 中，
                // 或者在组件中监听 settings.themeLabel 变化来执行。
            },
        }),
        {
            name: 'reader-storage', // 持久化存储的 key
            partialize: (state) => ({
                // 选择需要持久化的字段，例如只持久化 settings 和 fontSize
                settings: state.settings,
                fontSize: state.fontSize,
                bookmarked: state.bookmarked,
                activeBook: state.activeBook,
                activeBookId: state.activeBookId,
                currentChapter: state.currentChapter,
            }),
        }
    )
);

export default useZustandStore;